import React, { useState } from 'react';
import SavePresetModal from './SavePresetModal';
import { ApprovalStatus, Preset } from './models';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { prepareSql } from '../../../../utils';
import { appInsights } from '../../../../App';
import { environment } from '../../../../Config';
import { useAuthStore } from '../../../../features/authentication/stores/authStore';

const SavePresetButton: React.FC<{
  setName: string;
  companyName: string;
  discountDisplay: string;
  paintProvided: string;
  extraDiscount: number;
  unitDiscount: number;
  overallDiscount: number;
  margin: number;
  unitDiscountEnabled: string;
  minDiscount: number;
  paintDiscount: number;
  cleanDiscount: number;
  carpetCleanDiscount: number;
  maintenanceDiscount: number;
  saasUnitFee: number;
  saasContractedUnits: number;
  saasFeePeriod: string;
  saasPeriodCost: number;
  saasFeeEnabled: string;
  notes: string;
  originalName?: string;
  originalId?: number;
}> = ({
  setName,
  companyName,
  discountDisplay,
  paintProvided,
  extraDiscount,
  unitDiscount,
  overallDiscount,
  margin,
  unitDiscountEnabled,
  minDiscount,
  paintDiscount,
  cleanDiscount,
  carpetCleanDiscount,
  maintenanceDiscount,
  saasUnitFee,
  saasContractedUnits,
  saasFeePeriod,
  saasPeriodCost,
  saasFeeEnabled,
  notes,
  originalName,
  originalId,
}) => {
  const { username } = useAuthStore();
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const openModal = () => {
    if (notes.length === 0) {
      toast.error('Please add notes', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      return;
    }

    setModalIsOpen(true);

    appInsights.trackEvent({
      name: 'SavePreset',
    });
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const handleSubmit = (name: string) => {
    appInsights.trackEvent({
      name: 'SavePresetPopupSave',
      properties: {
        name: name,
        reason: notes,
      },
    });

    if (!name) {
      toast.error('Name cannot be empty', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      return;
    }

    if (!notes) {
      toast.error('Notes cannot be empty', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      return;
    }

    if (originalId) {
      handleEditPreset(name, false, originalId);
    } else {
      handleAddPreset(name, false);
    }

    closeModal();
  };

  const handleSubmitAndSend = (name: string) => {
    appInsights.trackEvent({
      name: 'SavePresetPopupSend',
      properties: {
        name: name,
        reason: notes,
      },
    });

    if (!name) {
      toast.error('Name cannot be empty', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      return;
    }

    if (!notes) {
      toast.error('Notes cannot be empty', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      return;
    }

    if (originalId) {
      handleEditPreset(name, true, originalId).then(v => {
        if (v != -1) {
          handleSend(originalId, name);
        }
      });
    } else {
      handleAddPreset(name, true).then(v => {
        if (v != -1) {
          handleSend(v, name);
        }
      });
    }

    closeModal();
  };

  const handleSend = async (id: number, name: string) => {
    try {
      const response = await fetch(
        environment === 'dev'
          ? `https://prod-49.eastus.logic.azure.com:443/workflows/500d66dea1e14e34ad6475bdba3e8524/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=hEzrJ8q_HIzncEXByM5t9PFlbusdvMfxk71qc4fpXzE`
          : `https://prod-72.eastus.logic.azure.com:443/workflows/0dcefd14b0aa4f12abfae1def3573c8f/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=S73oQWTY6n4K7NXwFTYi3FQyeknkMSR8pOhOJ-1aAUE`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: id,
            email: username,
            label: name,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to approve preset');
      }

      toast('Preset was sent on review', {
        position: 'top-right',
        autoClose: 2000,
        theme: 'colored',
        style: {
          color: 'rgba(16, 29, 59, 1)',
          fontFamily: 'SF Pro Display',
          fontWeight: '500',
        },
        progressStyle: {
          background: '#00AB9B',
        },
      });
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleAddPreset = async (name: string, isForApproval: boolean): Promise<number> => {
    try {
      const newPreset: Preset = {
        id: 0,
        name: prepareSql(name),
        user: username,
        company: companyName,
        set: setName,
        paintProvided: paintProvided,
        discountToDisplay: discountDisplay,
        extraDiscount: extraDiscount,
        approvalStatus: isForApproval ? ApprovalStatus.PENDING : ApprovalStatus.PRIVATE,
        unitDiscount: unitDiscount,
        overallDiscount: overallDiscount,
        margin: margin,
        unitDiscountEnabled: unitDiscountEnabled,
        reason: prepareSql(notes),
        rejectionReason: '',
        paintDiscount: paintDiscount,
        cleanDiscount: cleanDiscount,
        carpetCleanDiscount: carpetCleanDiscount,
        maintenanceDiscount: maintenanceDiscount,
        saasUnitFee: saasUnitFee,
        saasContractedUnits: saasContractedUnits,
        saasFeePeriod: saasFeePeriod,
        saasPeriodCost: saasPeriodCost,
        saasFeeEnabled: saasFeeEnabled,
        created: '',
        lastUpdated: '',
      };

      const response = await fetch(
        'https://prod-30.eastus.logic.azure.com:443/workflows/1a8cdf19cbed44cdbf7189e52fd40909/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=cpINvPeKDUlgGYDzPXAR_56wr1e_oJk-uvWwbEJbbuk',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newPreset),
        }
      );
      if (response.ok) {
        toast('Preset saved', {
          position: 'top-right',
          autoClose: 2000,
          theme: 'colored',
          style: {
            color: 'rgba(16, 29, 59, 1)',
            fontFamily: 'SF Pro Display',
            fontWeight: '500',
          },
          progressStyle: {
            background: '#00AB9B',
          },
        });

        let resp = await response.json();

        return resp.ResultSets.Table1[0].id;
      }
    } catch (error) {
      console.error('Error adding company:', error);

      toast.error("Couldn't save preset", {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }

    return -1;
  };

  const handleEditPreset = async (name: string, isForApproval: boolean, origId: number) => {
    try {
      const newPreset: Preset = {
        id: origId,
        name: prepareSql(name),
        user: '',
        company: companyName,
        set: setName,
        paintProvided: paintProvided,
        discountToDisplay: discountDisplay,
        extraDiscount: extraDiscount,
        approvalStatus: isForApproval ? ApprovalStatus.PENDING : ApprovalStatus.PRIVATE,
        unitDiscount: unitDiscount,
        overallDiscount: overallDiscount,
        margin: margin,
        unitDiscountEnabled: unitDiscountEnabled,
        reason: prepareSql(notes),
        rejectionReason: '',
        paintDiscount: paintDiscount,
        cleanDiscount: cleanDiscount,
        carpetCleanDiscount: carpetCleanDiscount,
        maintenanceDiscount: maintenanceDiscount,
        saasUnitFee: saasUnitFee,
        saasContractedUnits: saasContractedUnits,
        saasFeePeriod: saasFeePeriod,
        saasPeriodCost: saasPeriodCost,
        saasFeeEnabled: saasFeeEnabled,
        created: '',
        lastUpdated: '',
      };

      const response = await fetch(
        'https://prod-50.eastus.logic.azure.com:443/workflows/6e3c6bd2daea4d499ab913c98e157cd5/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=_ATRTg_U4dd_6-XcUbYY72BifxFaXITia8xv3s62cjY',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newPreset),
        }
      );
      if (response.ok) {
        toast('Preset saved', {
          position: 'top-right',
          autoClose: 2000,
          theme: 'colored',
          style: {
            color: 'rgba(16, 29, 59, 1)',
            fontFamily: 'SF Pro Display',
            fontWeight: '500',
          },
          progressStyle: {
            background: '#00AB9B',
          },
        });
      } else {
        throw new Error('Failed to edit preset');
      }
    } catch (error) {
      console.error('Error adding company:', error);

      toast.error("Couldn't save preset", {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      return -1;
    }
  };

  return (
    <div>
      {modalIsOpen && (
        <SavePresetModal
          onRequestClose={closeModal}
          onSubmit={handleSubmit}
          onSaveAndSend={handleSubmitAndSend}
          canSend={true}
          hasNotes={notes.length > 0}
          originalName={originalName}
        />
      )}
      <button style={{ ...styles.button, ...(originalName || originalId ? styles.copyStyle : {}) }} onClick={openModal}>
        {originalId ? 'Save Changes' : originalName ? 'Save Copy' : 'Save Preset'}
      </button>
    </div>
  );
};

const styles = {
  button: {
    height: '5.6vh',
    width: '24vh',
    fontSize: '1.6vh',
    fontWeight: '700',
    color: '#ffffff',
    backgroundColor: 'rgba(0, 171, 155, 1)', // Blue color
    border: 'none',
    borderRadius: '1.6vh',
    cursor: 'pointer',
    transition: 'background-color 0.3s, transform 0.2s',
    outline: 'none',
  },
  copyStyle: {
    color: '#00AB9B',
    backgroundColor: 'transparent',
    border: '2px solid #00AB9B',
  },
  buttonHover: {
    backgroundColor: '#2980b9', // Darker blue for hover
    transform: 'translateY(-0.2vh)',
  },
};

export default SavePresetButton;
